/**
 * useMyLeadRequests Hook
 *
 * Fetches lead requests for the current user
 */

import { useState, useEffect, useCallback } from 'react';
import { advisoryService } from 'services/advisoryService';
import { LeadRequest } from 'types/leadRequestsTypes';
import { useToast } from 'hooks/useToast';

interface UseMyLeadRequestsReturn {
  requests: LeadRequest[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useMyLeadRequests = (userId: string): UseMyLeadRequestsReturn => {
  const [requests, setRequests] = useState<LeadRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { toastError } = useToast();

  const fetchRequests = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await advisoryService.getLeadRequests({
        requester_user_id: userId,
        size: 50, // Fetch up to 50 requests
      });
      setRequests(response.items || []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to load your lead requests';
      setError(errorMessage);
      toastError(errorMessage);
      console.error('Error fetching lead requests:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, toastError]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  return {
    requests,
    loading,
    error,
    refetch: fetchRequests,
  };
};
